import { AnimatePresence, motion, PanInfo, Variants } from "framer-motion";
import { findIndex } from "lodash";
import { useRef, useState } from "react";
import styled from "styled-components";
import { useMouseOver } from "../../lib/hooks/useMouseOver";
import {
  Interaction,
  ThreeSixtyImage,
  ThreeSixtyInteraction,
} from "../../models/client";
import { fill } from "../../styles/utils";
import { CursorHolder } from "../CursorHolder";
import { LongCursor } from "../cursors/LongCursor";
import { InteractionTransition } from "../InteractionTransition";
import { Loader } from "../Loader";
import { Portal } from "../Portal";
import { ThreeSixtyImages } from "./ThreeSixtyImages";
import { ThreeSixtyInteractionToolTip } from "./ThreeSixtyInteractionToolTip";

interface ThreeSixtyProps {
  images: ThreeSixtyImage[];
}

const Boundary = styled(motion.div)`
  ${fill()}
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  flex-direction: column;
  /* cursor: none; */
  user-select: none;
`;

const Container = styled.div`
  position: relative;
  max-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  pointer-events: none;
`;
const HiddenImage = styled.img`
  position: relative;
  max-height: 100%;
  width: auto;
  max-width: 100%;
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  pointer-events: none;
`;

const LoaderPosition = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.1);
  z-index: 2;
  backdrop-filter: blur(8px);
  opacity: 0.9;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const threeSixtyVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export const ThreeSixty: React.FC<ThreeSixtyProps> = ({ images }) => {
  const [{ imageIndex, imageHelper }, setImageIndex] = useState({
    imageIndex: 0,
    imageHelper: images,
  });
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [selectedInteraction, setSelectedInteraction] =
    useState<Interaction | null>(null);
  const [limit, setLimit] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [exitComplete, setExitComplete] = useState(true);

  const hoverRef = useRef(null);
  const isHovered = useMouseOver(hoverRef);

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  const handlePan = (e: TouchEvent, info: PanInfo) => {
    if (info.offset.x > limit + 20) {
      const newLimit = limit + 20;
      setLimit(newLimit);
      next();
      return;
    }
    if (info.offset.x < limit - 20) {
      const newLimit = limit - 20;
      setLimit(newLimit);
      prev();
    }
  };

  const wrapImages = (index: number) => {
    return [...images.slice(index), ...images.slice(0, index)];
  };

  const handleImageLoad = (): void => {
    setImagesLoaded(imagesLoaded + 1);
  };

  const next = () => {
    const newImageIndex = imageIndex >= images.length - 1 ? 0 : imageIndex + 1;
    const newImageArray = wrapImages(newImageIndex);
    setImageIndex({ imageIndex: newImageIndex, imageHelper: newImageArray });
  };

  const prev = () => {
    const newImageIndex = imageIndex === 0 ? images.length - 1 : imageIndex - 1;
    const newImageArray = wrapImages(newImageIndex);
    setImageIndex({ imageIndex: newImageIndex, imageHelper: newImageArray });
  };

  const handleToolTipClick = (x: ThreeSixtyInteraction): void => {
    if (selectedInteraction) {
      setSelectedInteraction(null);
      return;
    }
    setExitComplete(false);
    if (x.type === "link") {
      const matchedIndex = findIndex(
        imageHelper,
        (image) => image.image.title === x.imageId
      );
      const distance = matchedIndex;
      const reverseDistance = images.length - distance;

      const lowerDistance =
        distance >= reverseDistance ? distance : reverseDistance;
      const direction = distance >= reverseDistance ? 1 : -1;
      const match = imageHelper[distance];
      let index = imageIndex;

      const interval = setInterval(() => {
        if (match.id === images[index].id) {
          clearInterval(interval);
          setSelectedInteraction(x.interaction);
          return;
        }
        if (direction === 1) {
          index = index === 0 ? images.length - 1 : index - 1;
        } else {
          index = index >= images.length - 1 ? 0 : index + 1;
        }
        const newImageArray = wrapImages(index);
        setImageIndex({ imageIndex: index, imageHelper: newImageArray });
      }, 200);
    } else {
      setSelectedInteraction(x.interaction);
    }
  };

  const handleInteractionComplete = () => {
    console.log("interaction cmplete");
    setExitComplete(true);
    setShowCursor(true);
    console.log(isHovered, showCursor, !selectedInteraction, exitComplete);
  };

  const showThreeSixty = () => !selectedInteraction && exitComplete;

  return (
    <Boundary
      onPan={selectedInteraction === null ? handlePan : () => {}}
      onPanEnd={() => setLimit(0)}
      id="drag"
      ref={hoverRef}
    >
      <AnimatePresence>
        {!isMobile &&
          isHovered &&
          showCursor &&
          !selectedInteraction &&
          exitComplete && (
            <Portal>
              <CursorHolder>
                <LongCursor icon="threeSixty">Drag to rotate</LongCursor>
              </CursorHolder>
            </Portal>
          )}
      </AnimatePresence>
      <Container>
        <HiddenImage src={images[0].image.src}></HiddenImage>

        <motion.div
          id="test"
          variants={threeSixtyVariants}
          animate={showThreeSixty() ? "visible" : "hidden"}
        >
          <ThreeSixtyImages
            images={images.map((image) => image.image)}
            currentIndex={imageIndex}
            onImageLoad={handleImageLoad}
          />
        </motion.div>

        {images[imageIndex].interaction && (
          <InteractionTransition
            imgStart={
              (images[imageIndex].interaction as ThreeSixtyInteraction).imgStart
            }
            imgEnd={
              (images[imageIndex].interaction as ThreeSixtyInteraction).imgEnd
            }
            selected={selectedInteraction !== null}
            onInteractionComplete={handleInteractionComplete}
          ></InteractionTransition>
        )}

        <AnimatePresence>
          {images[imageIndex].interaction &&
          !selectedInteraction &&
          exitComplete ? (
            <ThreeSixtyInteractionToolTip
              position={
                (images[imageIndex].interaction as ThreeSixtyInteraction)
                  .position
              }
              icon={"reply"}
              onClick={() =>
                handleToolTipClick(
                  images[imageIndex].interaction as ThreeSixtyInteraction
                )
              }
              onHoverStart={() => setShowCursor(false)}
              onHoverEnd={() => setShowCursor(true)}
            >
              Look inside
            </ThreeSixtyInteractionToolTip>
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {images[imageIndex].interaction && selectedInteraction ? (
            <ThreeSixtyInteractionToolTip
              position={
                (images[imageIndex].interaction as ThreeSixtyInteraction)
                  .position
              }
              icon={"reply"}
              onClick={() =>
                handleToolTipClick(
                  images[imageIndex].interaction as ThreeSixtyInteraction
                )
              }
              onHoverStart={() => setShowCursor(false)}
              onHoverEnd={() => setShowCursor(true)}
            >
              Back
            </ThreeSixtyInteractionToolTip>
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {imagesLoaded / images.length < 1 ? (
            <LoaderPosition
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Loader progress={imagesLoaded / images.length} />
            </LoaderPosition>
          ) : null}
        </AnimatePresence>
      </Container>
    </Boundary>
  );
};
